import React, { useState, useEffect } from 'react';
import s from './RoleManagement.module.css';

const RoleManagement = ({ organizationId }) => {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [actionType, setActionType] = useState(null); // 'assign' или 'revoke'

  // Загрузка участников организации и доступных ролей
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Загрузка участников организации
        const membersResponse = await fetch(`/api/organizations/${organizationId}/members`);
        const membersData = await membersResponse.json();
        setMembers(membersData);

        // Загрузка доступных ролей (теперь с ID)
        const rolesResponse = await fetch('/api/users-organizations/organization-roles');
        const rolesData = await rolesResponse.json();
        setRoles(rolesData);

      } catch (err) {
        setError('Ошибка загрузки данных');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  // Назначение роли пользователю
  const assignRole = async (userId, roleName) => {
    try {
      const response = await fetch('/api/users-organizations/assign-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          organizationId,
          roles: [roleName]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка назначения роли');
      }

      // Обновляем список участников
      const updatedMembers = await fetch(`/api/organizations/${organizationId}/members`);
      setMembers(await updatedMembers.json());

      alert('Роль успешно назначена!');

    } catch (err) {
      setError(err.message);
    }
  };

  // Снятие роли с пользователя
  const revokeRole = async (userId, roleName) => {
    if (!window.confirm(`Вы уверены, что хотите снять роль ${roleName} у пользователя?`)) return;

    try {
      const response = await fetch('/api/users-organizations/revoke-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          organizationId,
          roles: [roleName]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка снятия роли');
      }

      // Обновляем список участников
      const updatedMembers = await fetch(`/api/organizations/${organizationId}/members`);
      setMembers(await updatedMembers.json());

      alert('Роль успешно снята!');

    } catch (err) {
      setError(err.message);
    }
  };

  // Получить роли, которые можно назначить пользователю
  const getAssignableRoles = (member) => {
    const userRoleNames = (member.roles || []).map(r => r.name);
    return roles.filter(role => !userRoleNames.includes(role.name));
  };

  // Получить роли пользователя для снятия
  const getUserRoles = (member) => {
    return member.roles || [];
  };

  // Обработчик клика по кнопке действия
  const handleActionClick = (userId, type) => {
    if (expandedUser === userId && actionType === type) {
      // Если уже открыт тот же пользователь с тем же действием - закрываем
      setExpandedUser(null);
      setActionType(null);
    } else {
      // Открываем новый пользователь/действие
      setExpandedUser(userId);
      setActionType(type);
    }
  };

  if (loading) return <div className={s.loading}>Загрузка...</div>;
  if (error) return <div className={s.error}>{error}</div>;

  return (
      <div className={s.roleManagement}>
        <h3>Управление ролями</h3>

        <div className={s.membersList}>
          <h4>Участники организации</h4>

          <table className={s.membersTable}>
            <thead>
            <tr>
              <th>Пользователь</th>
              <th>Роли</th>
              <th>Действия</th>
            </tr>
            </thead>
            <tbody>
            {members.map(member => (
                <React.Fragment key={member.id}>
                  <tr>
                    <td>
                      {member.firstName} {member.lastName}
                      <div className={s.email}>{member.email}</div>
                    </td>
                    <td>
                      {member.roles && member.roles.length > 0 ? (
                        <div className={s.rolesContainer}>
                          {member.roles.map(role => (
                            <span key={role.id} className={s.roleBadge}>
                              {role.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className={s.noRole}>Нет ролей</span>
                      )}
                    </td>
                    <td className={s.actionsCell}>
                      <button
                          onClick={() => handleActionClick(member.id, 'assign')}
                          className={s.actionButton}
                          style={{ backgroundColor: '#4CAF50', color: 'white', marginRight: 8 }}
                      >
                        Назначить
                      </button>
                      <button
                          onClick={() => handleActionClick(member.id, 'revoke')}
                          className={s.actionButton}
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                      >
                        Разжаловать
                      </button>
                    </td>
                  </tr>
                  {/* Выпадающий список ролей */}
                  {expandedUser === member.id && (
                      <tr>
                        <td colSpan="3" className={s.expandedRow}>
                          <div className={s.rolesList}>
                            {actionType === 'assign' && (
                                <>
                                  <h5>Доступные роли для назначения:</h5>
                                  {getAssignableRoles(member).length > 0 ? (
                                      getAssignableRoles(member).map(role => (
                                          <div key={role.id} className={s.roleItem}>
                                            <span>{role.name}</span>
                                            <button
                                                onClick={() => assignRole(member.id, role.name)}
                                                className={s.assignRoleButton}
                                                style={{ backgroundColor: '#4CAF50', color: 'white' }}
                                            >
                                              Назначить
                                            </button>
                                          </div>
                                      ))
                                  ) : (
                                      <p>Нет доступных ролей для назначения</p>
                                  )}
                                </>
                            )}
                            {actionType === 'revoke' && (
                                <>
                                  <h5>Роли пользователя:</h5>
                                  {getUserRoles(member).length > 0 ? (
                                      getUserRoles(member).map(role => (
                                          <div key={role.id} className={s.roleItem}>
                                            <span>{role.name}</span>
                                            <button
                                                onClick={() => revokeRole(member.id, role.name)}
                                                className={s.revokeRoleButton}
                                                style={{ backgroundColor: '#f44336', color: 'white' }}
                                            >
                                              Разжаловать
                                            </button>
                                          </div>
                                      ))
                                  ) : (
                                      <p>У пользователя нет ролей для снятия</p>
                                  )}
                                </>
                            )}
                          </div>
                        </td>
                      </tr>
                  )}
                </React.Fragment>
            ))}
            </tbody>
          </table>
        </div>
      </div>
  );
};

export default RoleManagement;
